import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.35 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 1, ease: [0.76, 0, 0.24, 1] } },
};

const Line = ({ children, className = "" }) => (
  <span className="line-mask">
    <motion.span variants={lineChild} className={`block ${className}`}>
      {children}
    </motion.span>
  </span>
);

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen w-full overflow-hidden">
      {/* Parallax hero image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 z-0">
        <img
          src="/hero.png"
          alt="A gardener, librarian and banker crossing a bridge from a dark analog past into a colourful AI and robotics future — generated with Nano Banana"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C] via-[#0A0A0C]/40 to-[#0A0A0C]/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0C]/90 via-transparent to-transparent" />
      </motion.div>

      <motion.div
        style={{ y: textY }}
        className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 min-h-screen flex flex-col justify-end pb-20 pt-28"
      >
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="label-caps text-white/60 mb-6"
          data-testid="hero-eyebrow"
        >
          PivoT ransition · The Guide
        </motion.p>

        <motion.h1
          variants={lineParent}
          initial="hidden"
          animate="show"
          className="max-w-4xl text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight"
          data-testid="hero-title"
        >
          <Line className="font-origin font-light text-white/70">Gardener. Librarian.</Line>
          <Line className="font-origin italic font-light text-white/70">Banker.</Line>
          <Line className="font-future font-semibold text-white glow-text mt-2">Cross into AI,</Line>
          <Line className="font-future font-semibold text-white glow-text">Fintech & Robotics.</Line>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.9 }}
          className="mt-8 max-w-xl text-base sm:text-lg text-white/70 leading-relaxed"
          data-testid="hero-sub"
        >
          A practical field guide for crossing into the future of work. No full restart —
          just the right bridge roles, the right proof, and a 90-day plan.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-primary-cta"
            onClick={() => scrollTo("guides")}
            className="label-caps px-7 py-3.5 rounded-full bg-white text-black hover:bg-[var(--neon-cyan)] transition-colors duration-300"
          >
            Get the Guide
          </button>
          <button
            data-testid="hero-secondary-cta"
            onClick={() => scrollTo("inside")}
            className="label-caps px-7 py-3.5 rounded-full border border-white/25 text-white/80 hover:border-white hover:text-white transition-colors duration-300"
          >
            What&apos;s inside
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-6 lg:right-10 z-10 flex items-center gap-3 text-white/50"
      >
        <span className="label-caps">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.6 }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.div>
    </section>
  );
};
